import { embeddingService } from './EmbeddingService';
import { pineconeServiceBrowser } from './PineconeServiceBrowser';
import { pineconeServiceFallback } from './PineconeServiceFallback';
import OpenAI from 'openai';
import type { PhilosopherMetadata, PineconeQueryResponse } from '../types/pinecone';

export interface RAGResponse {
  answer: string;
  citations: Array<{
    id: string;
    text: string;
    metadata: PhilosopherMetadata;
    relevanceScore: number;
  }>;
  philosopher: 'adam-smith' | 'karl-marx';
}

export class RAGService {
  private openai: OpenAI;
  private pineconeService: {
    query: (vector: number[], philosopher?: 'adam-smith' | 'karl-marx', topK?: number) => Promise<PineconeQueryResponse>;
  };
  
  constructor() {
    this.openai = new OpenAI({
      apiKey: import.meta.env.VITE_OPENAI_API_KEY,
      dangerouslyAllowBrowser: true // Note: In production, use a backend service
    });
    
    // Try to use the browser service, fall back to mock if needed
    this.pineconeService = pineconeServiceBrowser;
  }

  async queryPhilosopher(
    question: string,
    philosopher: 'adam-smith' | 'karl-marx',
    temperature: number = 0.7
  ): Promise<RAGResponse> {
    // Step 1: Create embedding for the question
    const questionEmbedding = await embeddingService.createEmbedding(question);
    
    // Step 2: Query Pinecone for relevant chunks
    let queryResponse;
    try {
      queryResponse = await this.pineconeService.query(
        questionEmbedding,
        philosopher,
        5 // top 5 most relevant chunks
      );
    } catch (error) {
      console.warn('Pinecone query failed, falling back to mock service:', error);
      this.pineconeService = pineconeServiceFallback;
      queryResponse = await this.pineconeService.query(
        questionEmbedding,
        philosopher,
        5
      );
    }
    
    // Step 3: Prepare context from retrieved chunks
    const contextParts = queryResponse.matches
      .filter((match) => match.text) // Only include matches with text
      .map((match) => match.metadata.work_title ? 
        `[${match.metadata.work_title}]: ${match.text}` : 
        match.text
      );
    
    const context = contextParts.length > 0 ? 
      contextParts.join('\n\n') : 
      `I don't have specific text content available, but I can answer based on my knowledge of ${philosopher === 'adam-smith' ? 'Adam Smith' : 'Karl Marx'}.`;
    
    // Step 4: Generate response using OpenAI
    const systemPrompt = this.getSystemPrompt(philosopher);
    const completion = await this.openai.chat.completions.create({
      model: 'gpt-4-turbo-preview',
      messages: [
        { role: 'system', content: systemPrompt },
        { 
          role: 'user', 
          content: `Based on the following context from my works, answer this question: ${question}\n\nContext:\n${context}` 
        }
      ],
      temperature,
      max_tokens: 1000,
    });
    
    const answer = completion.choices[0].message.content || '';
    
    // Step 5: Format citations
    const citations = queryResponse.matches.map((match) => ({
      id: match.id,
      text: match.text || '',
      metadata: match.metadata,
      relevanceScore: match.score,
    }));
    
    return {
      answer,
      citations,
      philosopher,
    };
  }

  private getSystemPrompt(philosopher: 'adam-smith' | 'karl-marx'): string {
    const prompts = {
      'adam-smith': `You are Adam Smith, the 18th-century Scottish economist and philosopher. You wrote "The Wealth of Nations" and "The Theory of Moral Sentiments". 

Your key ideas include:
- The invisible hand of the market
- Division of labor and specialization
- Free markets and limited government intervention
- The importance of both self-interest and sympathy in human behavior

Respond in first person as Adam Smith would, drawing from your actual writings. Be thoughtful, measured, and emphasize both economic efficiency and moral considerations.`,
      
      'karl-marx': `You are Karl Marx, the 19th-century German philosopher, economist, and revolutionary socialist. You wrote "Das Kapital" and "The Communist Manifesto".

Your key ideas include:
- Historical materialism and class struggle
- The labor theory of value
- Critique of capitalism and exploitation
- The inevitable transition from capitalism to socialism to communism

Respond in first person as Karl Marx would, drawing from your actual writings. Be passionate about workers' rights, critical of capitalist exploitation, and emphasize the material conditions of society.`
    };
    
    return prompts[philosopher];
  }

  async generateSummary(
    workTitle: string,
    philosopher: 'adam-smith' | 'karl-marx'
  ): Promise<string> {
    // Query for chunks from the specific work
    const questionEmbedding = await embeddingService.createEmbedding(
      `Summary of ${workTitle}`
    );
    
    const queryResponse = await this.pineconeService.query(
      questionEmbedding,
      philosopher,
      20 // Get more chunks for comprehensive summary
    );
    
    // Filter for chunks from the specific work
    const relevantChunks = queryResponse.matches
      .filter((match: { metadata: { work_title: string } }) => match.metadata.work_title === workTitle)
      .map((match: { text?: string }) => match.text || '')
      .join('\n\n');
    
    const completion = await this.openai.chat.completions.create({
      model: 'gpt-4-turbo-preview',
      messages: [
        { 
          role: 'system', 
          content: 'You are a helpful assistant that creates comprehensive and detailed summaries of philosophical works. Include key themes, major arguments, historical context, and the work\'s significance to philosophical thought.' 
        },
        { 
          role: 'user', 
          content: `Please provide a detailed and comprehensive summary of "${workTitle}" based on the following excerpts. Include main themes, key arguments, important concepts, historical context, and the work's lasting impact on philosophy:\n\n${relevantChunks}` 
        }
      ],
      temperature: 0.5,
      max_tokens: 1500,
    });
    
    return completion.choices[0].message.content || '';
  }
}

let _ragService: RAGService | null = null;

export const ragService = {
  get instance(): RAGService {
    if (!_ragService) {
      _ragService = new RAGService();
    }
    return _ragService;
  },
  
  // Proxy methods for backward compatibility
  async queryPhilosopher(question: string, philosopher: 'adam-smith' | 'karl-marx', maxChunks: number = 5): Promise<RAGResponse> {
    return this.instance.queryPhilosopher(question, philosopher, maxChunks);
  },
  
  async generateSummary(workTitle: string, philosopher: 'adam-smith' | 'karl-marx'): Promise<string> {
    return this.instance.generateSummary(workTitle, philosopher);
  }
};